import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, CheckCircle, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const projectTypes = [
  'Kitchen Remodeling',
  'Bathroom Remodeling',
  'Interior Renovation',
  'Flooring',
  'Tile Work',
  'Painting',
  'Trim & Finish Carpentry',
  'Drywall Repair',
  'Doors & Windows',
  'Deck Repair',
  'Punch List / Make-Ready',
  'Rental Property Refresh',
  'Other',
];

const budgetRanges = [
  'Under $5,000',
  '$5,000 - $10,000',
  '$10,000 - $25,000',
  '$25,000 - $50,000',
  '$50,000 - $100,000',
  'Over $100,000',
  'Not Sure',
];

const timelines = [
  'ASAP',
  'Within 1 Month',
  '1-3 Months',
  '3-6 Months',
  '6+ Months',
  'Flexible',
];

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    project_type: '',
    budget_range: '',
    timeline: '',
    description: '',
  });
  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isValid = file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024;
      if (!isValid) {
        toast.error(`${file.name} is not a valid image or exceeds 10MB`);
      }
      return isValid;
    });
    setPhotos(prev => [...prev, ...validFiles].slice(0, 5));
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      photos.forEach(photo => {
        submitData.append('photos', photo);
      });

      await axios.post(`${API}/leads`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setIsSubmitted(true);
      toast.success('Your request has been submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <main data-testid="contact-page">
        {/* Hero */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#F9F9F7]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="w-16 h-px bg-[#C6A87C] mb-8" />
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6">Contact Us</h1>
          </div>
        </section>

        {/* Success Message */}
        <section className="py-24 md:py-32">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-20 h-20 mx-auto mb-8 flex items-center justify-center bg-[#C6A87C]/10 rounded-full">
                <CheckCircle className="w-10 h-10 text-[#C6A87C]" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl mb-4">Thank You!</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                We've received your request and will be in touch within 24 hours to discuss your project. We're excited to help bring your vision to life!
              </p>
              <div className="bg-[#F9F9F7] p-6 mb-8">
                <p className="text-gray-600 text-sm">
                  In the meantime, feel free to give us a call if you have any urgent questions:
                </p>
                <a
                  href="tel:9048636673"
                  className="inline-flex items-center gap-2 text-[#C6A87C] font-semibold mt-2 hover:underline"
                >
                  <Phone className="w-4 h-4" />
                  (904) 863-6673
                </a>
              </div>
              <button
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    address: '',
                    project_type: '',
                    budget_range: '',
                    timeline: '',
                    description: '',
                  });
                  setPhotos([]);
                }}
                className="text-sm text-gray-500 hover:text-[#C6A87C] underline"
              >
                Submit another request
              </button>
            </motion.div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="contact-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="w-16 h-px bg-[#C6A87C] mb-8" />
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6">Contact Us</h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              Ready to start your project? Fill out the form below or give us a call. We'll get back to you within 24 hours to discuss your vision.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              <h2 className="font-heading text-2xl mb-8">Get In Touch</h2>
              
              <div className="space-y-6 mb-12">
                <a
                  href="tel:9048636673"
                  data-testid="contact-phone"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-[#F9F9F7] group-hover:bg-[#C6A87C]/10 transition-colors">
                    <Phone className="w-5 h-5 text-[#C6A87C]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Phone</p>
                    <p className="text-lg font-medium group-hover:text-[#C6A87C] transition-colors">(904) 863-6673</p>
                  </div>
                </a>

                <a
                  href="mailto:jaxremodelingsolutions@gmail.com"
                  data-testid="contact-email"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-[#F9F9F7] group-hover:bg-[#C6A87C]/10 transition-colors">
                    <Mail className="w-5 h-5 text-[#C6A87C]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Email</p>
                    <p className="text-lg font-medium group-hover:text-[#C6A87C] transition-colors break-all">jaxremodelingsolutions@gmail.com</p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#F9F9F7]">
                    <MapPin className="w-5 h-5 text-[#C6A87C]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Location</p>
                    <p className="text-lg font-medium">St. Augustine, FL</p>
                    <p className="text-gray-500 text-sm">Serving Northeast Florida</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="aspect-square overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d220366.9694127147!2d-81.59567399999999!3d29.9012735!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e428b89d10b24f%3A0x1e55ceda80e8a01f!2sSt.%20Augustine%2C%20FL!5e0!3m2!1sen!2sus!4v1704067200000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Service Area Map"
                  className="grayscale"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-0 py-4 bg-transparent border-0 border-b border-gray-300 focus:border-[#C6A87C] focus:ring-0 outline-none transition-colors"
                      placeholder="Your full name"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-0 py-4 bg-transparent border-0 border-b border-gray-300 focus:border-[#C6A87C] focus:ring-0 outline-none transition-colors"
                      placeholder="(904) 000-0000"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-0 py-4 bg-transparent border-0 border-b border-gray-300 focus:border-[#C6A87C] focus:ring-0 outline-none transition-colors"
                      placeholder="your@email.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="address" className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                      Address / City
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full px-0 py-4 bg-transparent border-0 border-b border-gray-300 focus:border-[#C6A87C] focus:ring-0 outline-none transition-colors"
                      placeholder="St. Augustine, FL"
                      data-testid="input-address"
                    />
                  </div>
                </div>

                {/* Project Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="project_type" className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                      Project Type *
                    </label>
                    <select
                      id="project_type"
                      name="project_type"
                      value={formData.project_type}
                      onChange={handleInputChange}
                      required
                      className="w-full px-0 py-4 bg-transparent border-0 border-b border-gray-300 focus:border-[#C6A87C] focus:ring-0 outline-none transition-colors cursor-pointer"
                      data-testid="select-project-type"
                    >
                      <option value="">Select type</option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="budget_range" className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                      Budget Range
                    </label>
                    <select
                      id="budget_range"
                      name="budget_range"
                      value={formData.budget_range}
                      onChange={handleInputChange}
                      className="w-full px-0 py-4 bg-transparent border-0 border-b border-gray-300 focus:border-[#C6A87C] focus:ring-0 outline-none transition-colors cursor-pointer"
                      data-testid="select-budget"
                    >
                      <option value="">Select range</option>
                      {budgetRanges.map((range) => (
                        <option key={range} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="timeline" className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                      Timeline
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className="w-full px-0 py-4 bg-transparent border-0 border-b border-gray-300 focus:border-[#C6A87C] focus:ring-0 outline-none transition-colors cursor-pointer"
                      data-testid="select-timeline"
                    >
                      <option value="">Select timeline</option>
                      {timelines.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                    Project Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-0 py-4 bg-transparent border-0 border-b border-gray-300 focus:border-[#C6A87C] focus:ring-0 outline-none transition-colors resize-none"
                    placeholder="Tell us about your project, what you're looking to achieve, and any specific details..."
                    data-testid="input-description"
                  />
                </div>

                {/* Photo Upload */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
                    Upload Photos (Optional)
                  </label>
                  <div className="border-2 border-dashed border-gray-200 p-8 text-center hover:border-[#C6A87C] transition-colors">
                    <input
                      type="file"
                      id="photos"
                      multiple
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="input-photos"
                    />
                    <label htmlFor="photos" className="cursor-pointer">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-1">Click to upload photos</p>
                      <p className="text-gray-400 text-xs">Max 5 images, 10MB each</p>
                    </label>
                  </div>
                  {photos.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-4">
                      {photos.map((photo, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(photo)}
                            alt={`Upload ${index + 1}`}
                            className="w-20 h-20 object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center gap-3 bg-[#0A0A0A] text-white px-10 py-5 text-xs font-semibold uppercase tracking-widest hover:bg-[#C6A87C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Request
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mobile CTA */}
      <section className="py-12 bg-[#0A0A0A] lg:hidden">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-400 mb-4">Prefer to call?</p>
          <a
            href="tel:9048636673"
            data-testid="mobile-cta-phone"
            className="inline-flex items-center justify-center gap-3 bg-[#C6A87C] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors w-full"
          >
            <Phone className="w-4 h-4" />
            Call (904) 863-6673
          </a>
        </div>
      </section>
    </main>
  );
};
